/**
 * Trip lifecycle and billing rules for the admin app. Pure — no I/O.
 *
 * The database is the authority: guard_field_trip_status() and the billing
 * RPCs in 20260929120000_trip_lifecycle_and_billing.sql refuse anything this
 * file would not offer. This copy exists so the UI only shows actions that will
 * succeed. tests/tripLifecycle.test.ts fails if the two transition lists drift.
 */

export type TripStatus = 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
export type BillingBasis = 'hourly' | 'flat' | 'project' | 'contract';
export type BillingStatus = 'pending' | 'ready' | 'invoiced' | 'not_billable' | 'unreconciled';

export const TRIP_TRANSITIONS: Record<TripStatus, readonly TripStatus[]> = {
  scheduled: ['in_progress', 'completed', 'cancelled'],
  in_progress: ['completed', 'cancelled', 'scheduled'],
  completed: ['in_progress'],
  cancelled: ['scheduled'],
};

export function canTransition(from: TripStatus, to: TripStatus): boolean {
  return from === to || TRIP_TRANSITIONS[from].includes(to);
}

export interface TripAction {
  to: TripStatus;
  label: string;
  /** Destructive or hard-to-undo actions ask for confirmation. */
  confirm?: string;
  tone: 'primary' | 'neutral' | 'danger';
}

const ACTION_COPY: Record<string, Omit<TripAction, 'to'>> = {
  'scheduled>in_progress': { label: 'Start trip', tone: 'neutral' },
  'scheduled>completed': { label: 'Mark completed', tone: 'primary', confirm: 'Mark this trip completed? It moves to billing as ready to bill.' },
  'scheduled>cancelled': { label: 'Cancel trip', tone: 'danger', confirm: 'Cancel this trip? The customer is notified and it is marked not billable.' },
  'in_progress>completed': { label: 'Mark completed', tone: 'primary', confirm: 'Mark this trip completed? It moves to billing as ready to bill.' },
  'in_progress>cancelled': { label: 'Cancel trip', tone: 'danger', confirm: 'Cancel this trip? The customer is notified and it is marked not billable.' },
  'in_progress>scheduled': { label: 'Back to scheduled', tone: 'neutral' },
  'completed>in_progress': { label: 'Reopen', tone: 'neutral', confirm: 'Reopen this trip? It leaves the ready-to-bill queue until completed again.' },
  'cancelled>scheduled': { label: 'Reinstate', tone: 'neutral' },
};

/** The buttons to show for a trip, in display order. */
export function tripActions(status: TripStatus, billingStatus: BillingStatus | null): TripAction[] {
  return TRIP_TRANSITIONS[status]
    // An invoiced trip cannot be reopened until billing clears the invoice.
    .filter((to) => !(status === 'completed' && billingStatus === 'invoiced' && to === 'in_progress'))
    .map((to) => ({ to, ...ACTION_COPY[`${status}>${to}`] }));
}

/** Still open after its last day. Matches trip_billing_overview.is_overdue. */
export function isOverdue(
  t: { status: string | null; start_date: string | null; end_date: string | null },
  today: string,
): boolean {
  const last = t.end_date ?? t.start_date;
  return (t.status === 'scheduled' || t.status === 'in_progress') && last != null && last < today;
}

/** Today as YYYY-MM-DD in the viewer's timezone, comparable with DATE columns. */
export function localToday(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

// ─── Roles ───────────────────────────────────────────────────────────────────

const TERMS_ROLES = new Set(['account_manager', 'billing_admin', 'admin', 'superadmin']);
const OUTCOME_ROLES = new Set(['billing_admin', 'admin', 'superadmin']);

/** Matches set_trip_billing_terms(): who may say how a trip is billed. */
export function canSetBillingTerms(role: string | null | undefined): boolean {
  return Boolean(role && TERMS_ROLES.has(role));
}

/** Matches set_trip_billing_outcome(): who may record an invoice. */
export function canRecordInvoice(role: string | null | undefined): boolean {
  return Boolean(role && OUTCOME_ROLES.has(role));
}

// ─── Billing queue ───────────────────────────────────────────────────────────

/** The subset of trip_billing_overview the queue logic reads. */
export interface TripBillingRow {
  trip_status: TripStatus;
  billing_status: BillingStatus;
  billing_basis: BillingBasis | null;
  hourly_rate_cents: number | null;
  flat_fee_cents: number | null;
  labor_hours: number | string | null;
  reports_missing_hours: number | null;
  is_overdue: boolean;
}

export type QueueBucket = 'review' | 'terms' | 'ready' | 'history' | 'invoiced' | 'not_billable' | 'upcoming';

export const QUEUE_BUCKETS: { key: QueueBucket; label: string; hint: string }[] = [
  { key: 'review', label: 'Needs review', hint: 'Still open after the end date. Confirm it completed, or cancel or reschedule it.' },
  { key: 'terms', label: 'Missing terms', hint: 'Completed, but nobody has said how it is billed.' },
  { key: 'ready', label: 'Ready to bill', hint: 'Completed with billing terms. Invoice it in Zoho Books, then record the invoice number here.' },
  { key: 'history', label: 'Imported history', hint: 'Visits imported from Zoho deals and billed outside PazPortal. Record the Zoho Books invoice number to reconcile each one.' },
  { key: 'invoiced', label: 'Invoiced', hint: 'Invoice number recorded.' },
  { key: 'not_billable', label: 'Not billable', hint: 'Cancelled, covered by contract, or marked not billable.' },
  { key: 'upcoming', label: 'Scheduled / on site', hint: 'Open trips that are not overdue yet.' },
];

/** One bucket per trip, so every trip is counted exactly once. */
export function queueBucket(row: TripBillingRow): QueueBucket {
  if (row.billing_status === 'invoiced') return 'invoiced';
  if (row.billing_status === 'not_billable') return 'not_billable';
  if (row.billing_status === 'unreconciled') return 'history';
  if (row.trip_status === 'scheduled' || row.trip_status === 'in_progress') {
    return row.is_overdue ? 'review' : 'upcoming';
  }
  if (row.trip_status === 'completed') return row.billing_basis ? 'ready' : 'terms';
  return 'not_billable';
}

/** Reasons a ready-to-bill trip still deserves a second look before invoicing. */
export function billingWarnings(row: TripBillingRow): string[] {
  const warnings: string[] = [];
  const hours = toNumber(row.labor_hours);
  if (row.billing_basis === 'hourly') {
    if (row.hourly_rate_cents == null) warnings.push('No hourly rate set.');
    if (hours == null) warnings.push('No labor hours recorded on the daily reports.');
  }
  if (row.billing_basis === 'flat' && row.flat_fee_cents == null) warnings.push('No flat fee set.');
  if ((row.reports_missing_hours ?? 0) > 0 && row.billing_basis === 'hourly') {
    const n = row.reports_missing_hours ?? 0;
    warnings.push(`${n} submitted report${n === 1 ? '' : 's'} without labor hours.`);
  }
  return warnings;
}

export const BASIS_LABELS: Record<BillingBasis, string> = {
  hourly: 'Hourly labor',
  flat: 'Flat per trip',
  project: 'Against project / PO',
  contract: 'Covered by contract',
};

export const BILLING_STATUS_LABELS: Record<BillingStatus, string> = {
  pending: 'Pending',
  ready: 'Ready to bill',
  invoiced: 'Invoiced',
  not_billable: 'Not billable',
  unreconciled: 'Imported — unreconciled',
};

export function toNumber(v: number | string | null | undefined): number | null {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/** "$125.00" → 12500. Returns null for blank, NaN on anything unparseable. */
export function parseDollarsToCents(input: string): number | null {
  const s = input.replace(/[$,\s]/g, '');
  if (s === '') return null;
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return Number.NaN;
  return Math.round(Number(s) * 100);
}

